import { useRouter } from '@tanstack/react-router';
import type { Message, MessageToolResult } from '@/context/agent-state.context';
import {
  HARNESS_DOWNLOAD_FILE_TOOLS,
  parseHarnessActionResult,
} from '@erp/shared-types';
import { NamiChangeCard } from './nami/NamiChangeCard';
import { NamiFailedCard } from './nami/NamiFailedCard';
import { NamiPreviewCard } from './nami/NamiPreviewCard';
import { BarChartToolResult } from './BarChartToolResult';
import { StatTilesToolResult } from './StatTilesToolResult';
import { TableToolResult } from './TableToolResult';
import { SectionCardToolResult } from './SectionCardToolResult';
import { NavButtonToolResult } from './NavButtonToolResult';
import { NotificationsToolResult } from './NotificationsToolResult';
import { TasksToolResult } from './TasksToolResult';
import { DownloadFileToolResult } from './DownloadFileToolResult';
import { ShowViewToolResult } from './show-view/ShowViewToolResult';
import {
  findCatalogPage,
  type AgentCatalogPage,
} from '@/components/nami/agentPageCatalog';

/** Harness export tools whose result is a downloadable file. */
// Harness tools reach the model under their short chat name (see
// `harnessChatToolName`), so the stream's toolName is `export_sales_orders_csv`.
const DOWNLOAD_FILE_TOOLS = new Set(HARNESS_DOWNLOAD_FILE_TOOLS);

/**
 * Cards every chat surface can render, keyed by tool name, or `null` when the
 * tool has no card. Module-specific renderers (EDI pages) delegate here for
 * anything they don't map themselves, so a tool added for chat home also draws
 * on the docked panels. `pages` is the access-filtered navigation catalog.
 */
export function renderSharedToolResult(
  toolResult: MessageToolResult,
  pages: ChatToolResultPage[],
): React.ReactNode {
  if (DOWNLOAD_FILE_TOOLS.has(toolResult.toolName)) {
    return <DownloadFileToolResult result={toolResult.result} />;
  }
  // Assistant action tools share one result shape (applied / preview /
  // failed) whatever the tool, so they are matched on the result, not the name.
  const action = parseHarnessActionResult(toolResult.result);
  if (action) {
    switch (action.status) {
      case 'applied':
        return <NamiChangeCard result={action} />;
      case 'pending_user_approval':
        return <NamiPreviewCard result={action} />;
      case 'failed':
        return <NamiFailedCard result={action} />;
    }
  }
  switch (toolResult.toolName) {
    case 'render_bar_chart':
      return <BarChartToolResult args={toolResult.args} />;
    case 'render_stat_tiles':
      return <StatTilesToolResult args={toolResult.args} />;
    case 'render_table':
      return <TableToolResult args={toolResult.args} />;
    case 'render_section_card':
      return <SectionCardToolResult args={toolResult.args} pages={pages} />;
    case 'show_view':
      return (
        <ShowViewToolResult args={toolResult.args} result={toolResult.result} />
      );
    case 'navigate_to_page': {
      const path =
        typeof toolResult.args.path === 'string'
          ? toolResult.args.path
          : undefined;
      return path ? <CatalogNavButton path={path} pages={pages} /> : null;
    }
    case 'list_my_notifications':
      return <NotificationsToolResult result={toolResult.result} />;
    case 'list_my_tasks':
      return <TasksToolResult result={toolResult.result} />;
    default:
      return null;
  }
}

export type ChatToolResultPage = AgentCatalogPage;

/**
 * A nav button for a model-emitted path, or nothing when the path isn't in
 * the catalog: drop hallucinated/inaccessible paths rather than render a
 * broken button — the catalog is the source of truth.
 */
function CatalogNavButton({
  path,
  pages,
}: {
  path: string;
  pages: ChatToolResultPage[];
}) {
  const router = useRouter();
  const dest = findCatalogPage(router, pages, path);
  return dest ? <NavButtonToolResult to={path} label={dest.label} /> : null;
}

/**
 * Generative UI: renders the agent's tool calls as inline cards/buttons. The
 * `render_*` tools carry their payload in the tool-call args; the read tools
 * (notifications, tasks) render from the tool result. Anything else falls
 * through to the message list's default.
 *
 * Shared by the live chat and the read-only history transcript so a past
 * conversation replays exactly what it looked like when it happened.
 *
 * `pages` is the access-filtered navigation catalog. Model-emitted paths are
 * validated against it, so passing an empty catalog simply renders no nav
 * buttons rather than broken ones.
 */
export function useChatToolResultRenderer(pages: ChatToolResultPage[]) {
  return (_message: Message, toolResult: MessageToolResult) =>
    renderSharedToolResult(toolResult, pages);
}
